
import React from "react";
import ChatInput from "../ChatInput";
import { ActiveSidebar } from "../../types/chat-sidebar-types";
import { Button } from "@/components/ui/button";
import { ListTodo, Zap } from "lucide-react";
import { cn } from '@flowstate/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { type MessageAttachment, type ReasoningEffort, DEFAULT_GEMINI_FLASH_MODEL } from "@flowstate/types";
import { NotebookPicker } from "./NotebookPicker";
import { ContextWindowRing } from "@flowstate/ui";
import type { ContextWindowUsage } from "../../hooks/useContextWindowUsage";
import { ModelPicker } from "../ModelPicker";
import { QuestionCard } from "@/features/cortex/components/timeline/QuestionCard";

interface ChatInputAreaProps {
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  handleSendMessage: (text?: string) => Promise<void>;
  isLoading: boolean;
  currentConversationId: string | undefined;
  handleFileUploaded: (attachment: MessageAttachment) => void;
  pendingAttachments: MessageAttachment[];
  onSetActiveSidebar: (sidebar: ActiveSidebar) => void;
  autoExtractTasks?: boolean;
  onToggleAutoExtract?: () => void;
  selectedSpecialist?: string;
  onSpecialistChange?: (specialist: string | undefined) => void;
  selectedProvider?: 'gemini' | 'openrouter';
  onProviderChange?: (provider: 'gemini' | 'openrouter') => void;
  selectedModel?: string;
  onModelChange?: (model: string) => void;
  reasoningEffort?: ReasoningEffort;
  onReasoningEffortChange?: (effort: ReasoningEffort) => void;
  isGeminiDelegated?: boolean;
  backgroundMode?: boolean;
  onToggleBackgroundMode?: () => void;
  selectedNotebookId?: string;
  onSelectNotebook?: (notebookId: string | undefined) => void;
  contextUsage?: ContextWindowUsage;
  /** ask_user HITL card — set when the brain 202s with awaiting_input. */
  pendingQuestion?: {
    runId: string;
    question: string;
    options?: string[];
    allowFreeText?: boolean;
  } | null;
  onAnswerQuestion?: (opts: { approved?: boolean; value?: string }) => void;
}

export function ChatInputArea({
  input,
  setInput,
  handleSendMessage,
  isLoading,
  currentConversationId,
  handleFileUploaded,
  pendingAttachments,
  onSetActiveSidebar,
  autoExtractTasks = false,
  onToggleAutoExtract,
  selectedSpecialist,
  onSpecialistChange,
  selectedProvider = 'gemini',
  onProviderChange,
  selectedModel = DEFAULT_GEMINI_FLASH_MODEL,
  onModelChange,
  reasoningEffort,
  onReasoningEffortChange,
  isGeminiDelegated,
  backgroundMode = false,
  onToggleBackgroundMode,
  selectedNotebookId,
  onSelectNotebook,
  contextUsage,
  pendingQuestion,
  onAnswerQuestion,
}: ChatInputAreaProps) {
  return (
    <div className="flex-shrink-0 border-t border-border/20 bg-background/60 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]" data-testid="cortex-input-area">
      <div className="p-4">
        {/* ask_user question card — the run is paused until answered */}
        {pendingQuestion && onAnswerQuestion && (
          <div className="mb-3">
            <QuestionCard
              runId={pendingQuestion.runId}
              question={{
                question: pendingQuestion.question,
                options: pendingQuestion.options,
                allowFreeText: pendingQuestion.allowFreeText,
              }}
              onAnswer={(_runId, opts) => onAnswerQuestion(opts)}
            />
          </div>
        )}
        {/* Background mode indicator */}
        {backgroundMode && (
          <div className="mb-2 flex items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-1.5 text-xs text-amber-600 dark:text-amber-400">
            <Zap className="h-3 w-3 shrink-0" />
            <span>Background mode — Cortex will notify you when complete</span>
          </div>
        )}
        <ChatInput 
          input={input}
          setInput={setInput}
          handleSendMessage={handleSendMessage}
          isLoading={isLoading}
          conversationId={currentConversationId ?? undefined}
          onFileUploaded={handleFileUploaded}
          pendingAttachments={pendingAttachments}
          onWebSearchClick={() => {
            setInput(prev => prev + ' [search web]');
          }}
          onWorkflowClick={() => {
            onSetActiveSidebar('workflows');
          }}
          selectedSpecialist={selectedSpecialist}
          onSpecialistChange={onSpecialistChange}
          selectedProvider={selectedProvider}
          onProviderChange={onProviderChange}
          selectedModel={selectedModel}
          onModelChange={onModelChange}
          reasoningEffort={reasoningEffort}
          onReasoningEffortChange={onReasoningEffortChange}
          isGeminiDelegated={isGeminiDelegated}
        />
        {/* Toggle row: model picker pill + context rings + notebook grounding + task extraction + background mode */}
        <div className="flex items-center justify-between mt-2 gap-1.5 flex-wrap">
          {/* Left: 1-click model picker + context rings + notebook */}
          <div className="flex items-center gap-2 flex-wrap">
            {onProviderChange && onModelChange && (
              <ModelPicker
                selectedProvider={selectedProvider}
                onProviderChange={onProviderChange}
                selectedModel={selectedModel}
                onModelChange={onModelChange}
                reasoningEffort={reasoningEffort}
                onReasoningEffortChange={onReasoningEffortChange}
                isGeminiDelegated={isGeminiDelegated}
                disabled={isLoading}
                variant="pill"
              />
            )}
            {contextUsage && contextUsage.totalTokens > 0 && (
              <TooltipProvider>
                <div className="flex items-center gap-1.5">
                  {/* Input ring — 1 revolution = 1M tokens */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="relative inline-flex items-center justify-center h-7 w-7">
                        <ContextWindowRing
                          percentage={((contextUsage.inputTokens % 1_000_000) / 1_000_000) * 100}
                          size={26}
                          strokeWidth={2.5}
                        />
                        <span className="absolute text-[7px] font-bold text-foreground leading-none">
                          {contextUsage.inputTokens >= 1_000_000
                            ? (contextUsage.inputTokens / 1_000_000).toFixed(1)
                            : (contextUsage.inputTokens / 1_000).toFixed(0)}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p className="text-xs">Input: {contextUsage.inputTokens.toLocaleString()} tokens</p>
                    </TooltipContent>
                  </Tooltip>
                  {/* Output ring — 1 revolution = 1M tokens */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="relative inline-flex items-center justify-center h-7 w-7">
                        <ContextWindowRing
                          percentage={((contextUsage.outputTokens % 1_000_000) / 1_000_000) * 100}
                          size={26}
                          strokeWidth={2.5}
                        />
                        <span className="absolute text-[7px] font-bold text-foreground leading-none">
                          {contextUsage.outputTokens >= 1_000_000
                            ? (contextUsage.outputTokens / 1_000_000).toFixed(1)
                            : (contextUsage.outputTokens / 1_000).toFixed(0)}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p className="text-xs">Output: {contextUsage.outputTokens.toLocaleString()} tokens</p>
                    </TooltipContent>
                  </Tooltip>
                  {/* Context ring — percentage of max limit */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="relative inline-flex items-center justify-center h-7 w-7">
                        <ContextWindowRing
                          percentage={contextUsage.percentage}
                          size={26}
                          strokeWidth={2.5}
                        />
                        <span className="absolute text-[7px] font-bold text-foreground leading-none">
                          {Math.round(contextUsage.percentage)}%
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <div className="text-xs space-y-0.5">
                        <p>Total: {contextUsage.totalTokens.toLocaleString()} tokens</p>
                        <p>Limit: {contextUsage.maxTokens.toLocaleString()} tokens</p>
                        <p className="text-muted-foreground">{contextUsage.modelName}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            )}
            {onSelectNotebook && (
              <NotebookPicker
                selectedNotebookId={selectedNotebookId}
                onSelectNotebook={onSelectNotebook}
              />
            )}
          </div>
          {/* Right: mode toggles */}
          <div className="flex items-center gap-1.5">
          {/* Background mode toggle */}
          {onToggleBackgroundMode && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={onToggleBackgroundMode}
                    className={cn(
                      "h-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all duration-200",
                      backgroundMode
                        ? "bg-amber-500/10 text-amber-600 border border-amber-500/20 dark:text-amber-400"
                        : "bg-muted/50 text-muted-foreground border border-border/30 hover:bg-muted"
                    )}
                    aria-label={backgroundMode ? "Disable background mode" : "Enable background mode"}
                    data-testid="cortex-background-mode-toggle"
                  >
                    <Zap className="h-3 w-3" />
                    {backgroundMode ? "Background: On" : "Background"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>{backgroundMode ? "Background mode active — click to disable" : "Enable background mode for async jobs"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {/* Task extraction toggle */}
          {onToggleAutoExtract && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={onToggleAutoExtract}
                    className={cn(
                      "h-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all duration-200",
                      autoExtractTasks
                        ? "bg-primary/10 text-primary border border-primary/20"
                        : "bg-muted/50 text-muted-foreground border border-border/30 hover:bg-muted"
                    )}
                    aria-label={autoExtractTasks ? "Disable auto task extraction" : "Enable auto task extraction"}
                    data-testid="cortex-task-extraction-toggle"
                  >
                    <ListTodo className="h-3 w-3" />
                    {autoExtractTasks ? "Tasks: On" : "Tasks: Off"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>{autoExtractTasks ? "Auto-extract action items from responses (click to disable)" : "Enable automatic task extraction from AI responses"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
